import { useCallback, useMemo, useState } from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';

import { useMailchimpStatus } from '../hooks/useMailchimpStatus';
import { DisconnectConfirmation } from './DisconnectConfirmation';
import { FieldMappingManager } from './FieldMappingManager';
import { SyncHealthPanel } from './SyncHealthPanel';

interface ConnectionStatusPanelProps {
  orgId: number | string;
}

export function ConnectionStatusPanel({ orgId }: ConnectionStatusPanelProps) {
  const { data, isLoading } = useMailchimpStatus(orgId);
  const { hasPermissionGroup } = usePermissions();
  const { schema: propertySchema } = usePropertySchema(orgId, 'ahoy_contact');
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);
  const [managingMappings, setManagingMappings] = useState(false);

  const availableAhoyProperties = useMemo(
    () =>
      Object.values(propertySchema ?? {})
        .filter(
          p =>
            p.value_write_access !== 'none' &&
            p.value_write_access !== 'internal'
        )
        .map(p => ({ name: p.property_type_name, label: p.label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [propertySchema]
  );

  const handleDisconnectClick = useCallback(() => {
    setIsDisconnectOpen(true);
  }, []);

  const handleDisconnectClose = useCallback(() => {
    setIsDisconnectOpen(false);
  }, []);

  const handleEditMappings = useCallback(() => {
    setManagingMappings(true);
  }, []);

  const handleBackFromMappings = useCallback(() => {
    setManagingMappings(false);
  }, []);

  if (isLoading) {
    return <LoadingIndicator size='sm' />;
  }

  if (!data?.connected) {
    return null;
  }

  const isAdmin = hasPermissionGroup('ahoy_other_admin');

  if (managingMappings) {
    return (
      <div className='card-refined p-6'>
        <FieldMappingManager
          orgId={orgId}
          availableAhoyProperties={availableAhoyProperties}
          onBack={handleBackFromMappings}
        />
      </div>
    );
  }

  const isSyncing = data.bulk_completed_at == null;
  const lastSyncText = data.last_sync_time
    ? new Date(data.last_sync_time).toLocaleString()
    : null;

  return (
    <div className='card-refined p-6'>
      <div className='flex items-start justify-between gap-4'>
        <div className='flex-1 min-w-0'>
          <p className='text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]'>
            Mailchimp audience
          </p>
          <h3 className='text-heading-3 text-[var(--color-text-primary)] truncate'>
            {data.audience_name ?? 'Not selected'}
          </h3>
          {typeof data.total_contacts_synced === 'number' && (
            <p className='mt-1 text-body-small text-[var(--color-text-secondary)]'>
              {data.total_contacts_synced.toLocaleString()} contacts synced
            </p>
          )}
          {lastSyncText && (
            <p className='mt-1 text-body-small text-[var(--color-text-secondary)]'>
              Last synced {lastSyncText}
            </p>
          )}
        </div>
        {isAdmin && (
          <div className='flex shrink-0 items-center gap-2'>
            <Button color='secondary' size='sm' onClick={handleEditMappings}>
              Edit field mappings
            </Button>
            <Button color='secondary' size='sm' onClick={handleDisconnectClick}>
              Disconnect
            </Button>
          </div>
        )}
      </div>

      <div className='mt-4'>
        {isSyncing ? (
          <div className='flex items-center gap-2'>
            <LoadingIndicator size='xs' inline />
            <span className='text-body-small text-[var(--color-text-secondary)]'>
              Initial import in progress…
            </span>
          </div>
        ) : (
          <Badge type='pill-color' color='success'>
            Initial import complete
          </Badge>
        )}
      </div>

      <SyncHealthPanel orgId={orgId} />

      <DisconnectConfirmation
        orgId={orgId}
        isOpen={isDisconnectOpen}
        onClose={handleDisconnectClose}
        audienceName={data.audience_name}
      />
    </div>
  );
}
